import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeftIcon, HelpCircleIcon } from 'lucide-react';
import Button from '../ui/Button';
import { flattenDestinations } from '../../config/navigation';
interface PageHeaderProps {
  title: string;
  description?: string;
  backLink?: string;
  backLabel?: string;
  actions?: React.ReactNode;
  helpText?: string;
}
const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  backLink,
  backLabel = 'Back',
  actions,
  helpText
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const handleBack = () => {
    if (backLink) {
      navigate(backLink);
    } else {
      navigate(-1);
    }
  };
  // Auto-derive a "Group › Page" breadcrumb from the current route (via the shared
  // nav config), so every page gets orientation without per-page wiring. Hidden
  // when a backLink is shown (that already provides context) or no match is found.
  const dest = flattenDestinations().find((d) => d.path === location.pathname);
  const showCrumb = !backLink && dest && dest.group !== dest.name;
  return <div className="mb-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          {backLink && <button onClick={handleBack} className="inline-flex items-center gap-1 text-body-sm font-semibold text-navy-600 hover:text-navy-700 mb-1.5">
              <ChevronLeftIcon className="h-4 w-4" />
              {backLabel}
            </button>}
          {showCrumb && (
            <nav aria-label="Breadcrumb" className="mb-1 text-meta text-ink-400">
              <span>{dest!.group}</span>
              <span className="mx-1.5" aria-hidden="true">›</span>
              <span className="text-ink-500">{dest!.name}</span>
            </nav>
          )}
          <h1 className="font-serif text-page-title text-navy-950">{title}</h1>
          {description && <p className="mt-1.5 text-body text-ink-500">{description}</p>}
        </div>
        {actions && <div className="flex-shrink-0">{actions}</div>}
      </div>
      {helpText && <div className="mt-4 flex gap-3 p-4 rounded-lg border bg-info-bg border-info-border">
          <HelpCircleIcon className="h-[18px] w-[18px] text-info-solid flex-shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-body-sm text-info-fg">{helpText}</p>
        </div>}
    </div>;
};
export default PageHeader;